import { useEffect, useRef, useState, type RefObject } from 'react';
import { DCHost } from './dc/runtime';
import { restoreSession, session, type SessionUser } from './api/auth';
import { isDesktop } from './platform';
import { serveSession, borrowMainSession } from './desktop/bridge';
import { serveTray, TrayWindow } from './desktop/tray';
import LoginLogic from './screens/login/logic';
import * as loginTpl from './screens/login/template.gen';
import ComptaLogic from './screens/compta/logic';
import * as comptaTpl from './screens/compta/template.gen';
import PayAssistantLogic from './screens/payassistant/logic';
import * as payAssistantTpl from './screens/payassistant/template.gen';
import { PasswordScreen } from './screens/password/PasswordScreen';
import { SignInScreen } from './screens/signin/SignInScreen';
import { api, MOCK } from './api/client';
import { DateAssist } from './components/date/DateAssist';
import { SuiteSwitcher } from './suiteSwitcher';
import { ContextMenu } from '@rimocore/shared/ctx/ContextMenu';
import { AppContextMenu } from './components/ctx/AppContextMenu';
import { AddressSuggest, type Suggestion } from '@rimocore/shared/geo/AddressSuggest';
import { DocEditHost } from './components/edit/DocEditDialog';
import { DocDeleteHost } from './components/edit/DocDeleteDialog';
import { BinderHost } from './components/print/BinderDialog';
import { PrintOverviewHost } from './components/print/PrintOverview';
import { ServerDialogHost } from './components/server/ServerDialog';
import { RecurringHost } from './components/recurring/RecurringDialog';
import { OrderHost } from './components/orders/OrderDialog';
import { CardStartHost } from './components/cards/CardStartDialog';

// Hash routing works identically in the browser and in the Tauri shell.
function useHash() {
  const [hash, setHash] = useState(location.hash);
  useEffect(() => {
    const on = () => setHash(location.hash);
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  return hash;
}

/** Desktop tray window: borrows the main window's session, never shows a login. */
function PayAssistantWindow() {
  useState(borrowMainSession);
  return <><DCHost key="pa" logic={PayAssistantLogic} template={payAssistantTpl.template} css={payAssistantTpl.css} /><ContextMenu /></>;
}

/**
 * Main window: the screens plus the own right-click menu (components/ctx), which reads the compta screen's logic, and
 * the suite address search (shared/geo, Vertrag X) for fields marked `data-addr` – today Personnel's address, French
 * like Personnel's UI, only on its button („click“ mode, private addresses).
 */
function MainWindow() {
  const compta = useRef<DCHost>(null);
  // AD: the tray menu's entries run the compta screen's own handlers.
  useEffect(() => serveTray((id) => {
    const l = compta.current?.logic as { newImport?: (dst?: string) => void; setState?: (s: object) => void } | undefined;
    if (id === 'import') l?.newImport?.('in');
    else if (id === 'statement') l?.newImport?.('ext');
    else if (id === 'dashboard') l?.setState?.({ page: 'browser' });
  }), []);
  return <><MainScreens comptaRef={compta} /><AppContextMenu getLogic={() => compta.current?.logic ?? null} />
    <AddressSuggest search={(q) => api<Suggestion[]>('/geo/search', { query: { q } })} lang="fr" /></>;
}

function MainScreens({ comptaRef }: { comptaRef: RefObject<DCHost | null> }) {
  const hash = useHash();
  const [user, setUser] = useState<SessionUser | null>(session.user);
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    const off = session.subscribe(setUser);
    const offServe = serveSession();
    restoreSession().finally(() => setBooting(false));
    return () => {
      off();
      offServe();
    };
  }, []);

  const resetToken = hash.startsWith('#/reset-password') ? new URLSearchParams(hash.split('?')[1] || '').get('token') : null;

  // Keep the URL in sync with the auth state.
  useEffect(() => {
    if (booting || (resetToken && MOCK)) return;
    if (!user && !hash.startsWith('#/login')) location.replace('#/login');
    // Signed in via RimoID: the login route has nothing to show any more.
    if (user && !MOCK && hash.startsWith('#/login')) location.replace('#/');
  }, [user, hash, booting, resetToken]);

  // Password reset links come from RimoID now (its own page); an old RimoCompta link only works in mock mode.
  if (resetToken && MOCK) return <PasswordScreen key="reset" mode={{ kind: 'reset', token: resetToken }} />;
  if (booting) return <div style={{ height: '100%', background: '#231F20' }} />;
  // Temporary password: nothing else is reachable until it is changed.
  if (user?.mustChangePassword && MOCK) return <PasswordScreen key="change" mode={{ kind: 'change' }} />;

  // Signed out: RimoID signs in (shared sign-in of the suite). Mock mode keeps the design's own login.
  if (!user || (MOCK && hash === '#/login')) {
    if (!MOCK) return <SignInScreen key={hash} />;
    return <DCHost key="login" logic={LoginLogic} template={loginTpl.template} css={loginTpl.css} />;
  }
  return <><DCHost key="compta" ref={comptaRef} logic={ComptaLogic} template={comptaTpl.template} css={comptaTpl.css} props={{ user }} /><DateAssist /><DocEditHost /><DocDeleteHost /><BinderHost /><ServerDialogHost /><PrintOverviewHost /><RecurringHost /><OrderHost /><CardStartHost />{!MOCK && <SuiteSwitcher />}</>;
}

export function App() {
  if (isDesktop() && location.hash.startsWith('#/pay-assistant')) return <PayAssistantWindow />;
  if (isDesktop() && location.hash.startsWith('#/tray')) return <TrayWindow />; // AD: the menu bar quick menu
  return <MainWindow />;
}
